import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Package, Truck, Loader2, ShieldAlert, RefreshCw, Search, X, Mail, Sparkles } from "lucide-react";

const STATUS_PILL = {
  printing: "bg-yellow-500/10 gold-text border-yellow-500/20",
  mailed: "bg-primary/10 text-primary border-primary/20",
  in_transit: "bg-primary/10 text-primary border-primary/20",
  delivered: "bg-secondary/15 text-secondary border-secondary/30",
  returned: "bg-red-500/10 text-red-400 border-red-500/20",
};

/**
 * Fulfillment View — the Director's direct look at EVERY successful send — physical (Lob)
 * AND digital — across ALL users, platform-wide, so any send can be visually verified.
 * Root Admin only (backend enforces this too — a 403 here means the account isn't the
 * Root Admin). Includes a "Search by Email" bar so a specific customer's history can be
 * pulled up instantly if they ever report an issue.
 */
const Fulfillment = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [filter, setFilter] = useState("");
  const [emailQuery, setEmailQuery] = useState("");
  const [emailInput, setEmailInput] = useState("");

  const load = async (status, email) => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (email) params.email = email;
      const r = await api.get("/admin/fulfillment-queue", { params });
      setData(r.data);
      setDenied(false);
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(filter, emailQuery); }, [filter, emailQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const runSearch = (e) => {
    e.preventDefault();
    setEmailQuery(emailInput.trim());
  };
  const clearSearch = () => { setEmailInput(""); setEmailQuery(""); };

  if (denied) {
    return (
      <AppShell>
        <div className="surface rounded-[2rem] p-16 text-center" data-testid="fulfillment-denied">
          <ShieldAlert className="w-12 h-12 mx-auto mb-4 text-red-400" />
          <h3 className="font-serif text-3xl mb-2">Root Admin only</h3>
          <p className="text-muted-foreground">The Fulfillment View is exclusive to the Supreme Director.</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-4xl mb-1 flex items-center gap-3">Fulfillment View <Package className="w-7 h-7 text-primary" /></h1>
          <p className="text-muted-foreground">Every successful send — physical &amp; digital — across the entire platform.</p>
        </div>
        <button onClick={() => load(filter, emailQuery)} className="flex items-center gap-2 surface rounded-2xl px-4 py-2.5 text-primary hover:border-primary/40">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Search by Email — instantly pull up a specific customer's fulfillment history */}
      <form onSubmit={runSearch} className="flex items-center gap-2 mb-6 max-w-md" data-testid="fulfillment-email-search">
        <div className="relative flex-1">
          <Mail className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="fulfillment-email-input"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            placeholder="Search by customer email…"
            className="w-full bg-input border border-border rounded-full pl-9 pr-9 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
          {emailQuery && (
            <button type="button" onClick={clearSearch} data-testid="fulfillment-email-clear"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <button type="submit" data-testid="fulfillment-email-submit"
          className="flex items-center gap-1.5 bg-gradient-to-r from-primary to-secondary text-white rounded-full px-4 py-2.5 text-sm hover:opacity-90">
          <Search className="w-4 h-4" /> Search
        </button>
      </form>
      {emailQuery && (
        <div className="text-xs text-muted-foreground mb-4 -mt-2" data-testid="fulfillment-email-active">
          Showing results for <span className="font-medium text-foreground">&quot;{emailQuery}&quot;</span>
        </div>
      )}

      {loading ? (
        <div className="text-center py-24 text-muted-foreground flex items-center justify-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Loading the queue…</div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-6" data-testid="fulfillment-filters">
            <button onClick={() => setFilter("")} className={`text-xs px-3 py-1.5 rounded-full border ${!filter ? "bg-primary/15 text-primary border-primary/30" : "surface text-muted-foreground"}`}>
              All ({Object.values(data?.counts || {}).reduce((a, b) => a + b, 0)})
            </button>
            {Object.entries(data?.counts || {}).map(([s, n]) => (
              <button key={s} onClick={() => setFilter(s)} className={`text-xs px-3 py-1.5 rounded-full border ${filter === s ? "bg-primary/15 text-primary border-primary/30" : "surface text-muted-foreground"}`}>
                {s} ({n})
              </button>
            ))}
            {data?.counts_by_method && (
              <span className="text-xs surface rounded-full px-3 py-1.5 flex items-center gap-1.5" data-testid="fulfillment-method-counts">
                <Truck className="w-3 h-3" /> {data.counts_by_method.physical || 0} physical
                <span className="text-border">·</span>
                <Sparkles className="w-3 h-3" /> {data.counts_by_method.digital || 0} digital
              </span>
            )}
            <span className="text-xs text-muted-foreground ml-auto flex items-center gap-1.5">Lob: {data?.lob_live ? <span className="text-secondary">LIVE</span> : <span className="gold-text">DEMO</span>}</span>
          </div>

          {(data?.items || []).length === 0 ? (
            <div className="surface rounded-[2rem] p-16 text-center">
              <Truck className="w-12 h-12 mx-auto mb-4 text-primary/60" />
              <h3 className="font-serif text-2xl mb-2">Queue is empty</h3>
              <p className="text-muted-foreground">{emailQuery ? "No sends match that email." : "No sends match this filter yet."}</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="fulfillment-queue">
              {data.items.map((c) => (
                <div key={c.card_id} className="surface rounded-[1.5rem] overflow-hidden card-soft" data-testid="fulfillment-item">
                  {c.image_thumb_base64 && (
                    <img src={`data:image/jpeg;base64,${c.image_thumb_base64}`} alt="" className="w-full h-36 object-cover" />
                  )}
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                        {c.method === "digital" ? <Sparkles className="w-3 h-3" /> : <Truck className="w-3 h-3" />} {c.occasion}
                      </span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_PILL[c.status] || "bg-muted/50 border-border"}`}>{c.status}</span>
                    </div>
                    <div className="font-serif text-lg">{c.recipient_name}</div>
                    <div className="text-xs text-muted-foreground mb-2">from {c.sender_name} · {c.sender_email}</div>
                    {c.tracking_number && <div className="text-[11px] text-muted-foreground">Tracking: {c.tracking_number}</div>}
                    {c.expected_delivery_date && <div className="text-[11px] text-muted-foreground">ETA: {c.expected_delivery_date}</div>}
                    {c.has_corporate_seal && <span className="text-[10px] gold-text">Corporate Seal</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </AppShell>
  );
};

export default Fulfillment;
